import { apiService } from "./api.service";

export interface ClientUser {
  id: string;
  email: string;
  name: string;
  role: "client";
  isActive: boolean;
  avatarUrl?: string | null;
  phone?: string | null;
  balance?: number;
  company?: string | null;
  taxId?: string | null;
  address?: string | null;
  lastLogin?: number | null;
}

export interface ClientLoginResult {
  success: boolean;
  data?: {
    user: ClientUser;
    accessToken: string;
    sessionId: string;
    csrfToken?: string;
  };
  mfaRequired?: boolean;
  mfaToken?: string;
}

export interface RegisterPendingResult {
  requiresVerification: boolean;
  email: string;
  message: string;
  expiresInSeconds: number;
}

const API_URL = import.meta.env.VITE_API_URL || "https://worker-auth.betalog-ventas.workers.dev";

export const clientAuthService = {
  /**
   * Paso 1: crea cliente pendiente y dispara envío de OTP.
   */
  async registerPending(data: {
    email: string;
    name: string;
    password: string;
    phone?: string;
  }): Promise<RegisterPendingResult> {
    const response = await apiService.fetchWithAuth("/auth/client/register", {
      method: "POST",
      body: JSON.stringify(data),
      skipAuth: true,
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Error al registrar");

    return result.data as RegisterPendingResult;
  },

  /**
   * Reenvía el código OTP.
   */
  async resendVerificationCode(email: string): Promise<{ message: string; expiresInSeconds: number }> {
    const response = await apiService.fetchWithAuth("/auth/client/send-verification-code", {
      method: "POST",
      body: JSON.stringify({ email }),
      skipAuth: true,
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Error al reenviar código");
    return result.data;
  },

  /**
   * Paso 2: valida OTP y activa la cuenta. Devuelve sesión completa.
   */
  async verifyEmail(email: string, code: string): Promise<ClientLoginResult> {
    const response = await apiService.fetchWithAuth("/auth/client/verify-email", {
      method: "POST",
      body: JSON.stringify({ email, code }),
      skipAuth: true,
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Error al verificar código");

    if (result.data?.accessToken) {
      apiService.setToken(result.data.accessToken);
      apiService.setSessionId(result.data.sessionId);
      if (result.data.csrfToken) apiService.setCsrfToken(result.data.csrfToken);
      apiService.setClient(result.data.user);
    }

    return result;
  },

  async login(email: string, password: string): Promise<ClientLoginResult> {
    const response = await apiService.fetchWithAuth("/auth/client/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      skipAuth: true,
    });

    const result = await response.json();
    if (!response.ok) {
      if (result.mfaRequired) {
        return { success: false, mfaRequired: true, mfaToken: result.data?.mfaToken };
      }
      // Cuenta no verificada
      if (response.status === 403 && result.requiresVerification) {
        const err: any = new Error(result.error || "Cuenta no verificada");
        err.requiresVerification = true;
        err.email = result.email;
        throw err;
      }
      throw new Error(result.error || "Error al iniciar sesión");
    }

    if (result.data?.accessToken) {
      apiService.setToken(result.data.accessToken);
      apiService.setSessionId(result.data.sessionId);
      if (result.data.csrfToken) apiService.setCsrfToken(result.data.csrfToken);
      apiService.setClient(result.data.user);
    }

    return result;
  },

  async verifyMFA(mfaToken: string, code: string): Promise<ClientLoginResult> {
    const response = await apiService.fetchWithAuth("/auth/client/mfa/verify", {
      method: "POST",
      body: JSON.stringify({ mfaToken, code }),
      skipAuth: true,
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Código MFA inválido");

    if (result.data?.accessToken) {
      apiService.setToken(result.data.accessToken);
      apiService.setSessionId(result.data.sessionId);
      if (result.data.csrfToken) apiService.setCsrfToken(result.data.csrfToken);
      apiService.setClient(result.data.user);
    }

    return result;
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await apiService.fetchWithAuth("/auth/client/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Error al enviar enlace");
    return result;
  },

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await apiService.fetchWithAuth("/auth/client/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, newPassword }),
      skipAuth: true,
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Error al restablecer contraseña");
    return result;
  },

  async fetchMe(): Promise<ClientUser | null> {
    const response = await apiService.fetchWithAuth("/auth/client/me");
    if (!response.ok) return null;
    const result = await response.json();
    if (result.data) {
      apiService.setClient(result.data);
      return result.data;
    }
    return null;
  },

  async logout(): Promise<void> {
    try {
      await apiService.fetchWithAuth("/auth/client/logout", { method: "POST" });
    } catch (e) {
      console.error("Logout error:", e);
    }
    apiService.clearSession();
  },
};